import { useMemo } from 'react';
import { RouterProvider } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { TooltipProvider } from '@/components/ui/tooltip';
import { HumaiBadge } from '@/components/layout/HumaiBadge';
import { QueryProvider } from '@/app/providers/query-provider';
import { createAppRouter } from '@/app/router';
import { i18n } from '@/shared/i18n';

/**
 * The application root: the provider stack the whole tree depends on, then the
 * data router.
 *
 * - `QueryProvider` owns the TanStack Query client (server data keyed by filter
 *   signature; session bootstrap).
 * - `TooltipProvider` is shadcn/ui's tooltip context, required once at the root.
 * - `RouterProvider` mounts the data router, whose shell route runs the `/api/me`
 *   bootstrap and holds the error boundary.
 * - `I18nextProvider` binds the app's single i18next instance into the tree so
 *   every `useTranslation`/`Trans` reads the externalized English catalogue
 *   (R18.9). The instance is initialised synchronously, so the first render
 *   already has real text.
 */
export function App(): React.JSX.Element {
  const router = useMemo(() => createAppRouter(), []);
  return (
    <I18nextProvider i18n={i18n}>
      <QueryProvider>
        <TooltipProvider>
          <RouterProvider router={router} />
          <HumaiBadge />
        </TooltipProvider>
      </QueryProvider>
    </I18nextProvider>
  );
}
